/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from "react";
import ConstraintCard from "./ConstraintCard";
import FieldPillPicker from "./FieldPillPicker";

/**
 * Props:
 *   value            — array of { name, fields: [], where?: string }
 *                      `where` makes the rule conditional: unique only among the rows
 *                      matching it (e.g. active records), so cancelled/archived rows
 *                      stop blocking a re-entry. Emitted as a partial unique index.
 *   onChange         — (newArray) => void
 *   availableColumns — array of { name, data_type }
 *   hasRows          — boolean; when true, add/edit disabled (remove-only)
 *   onEditingChange  — (bool) => void; reports whether an editor is open
 */
export default function UniqueConstraintBuilder({ value = [], onChange, availableColumns = [], hasRows = false, onEditingChange }) {
  // editing = { index: number | "new", data: { name, fields } } | null
  const [editing, setEditing] = useState(null);

  // The clause becomes part of the index definition, so it cannot be parameterised.
  // Mirrors the server-side guard — reject anything that could carry a second statement.
  const WHERE_BANNED = [[";", "a semicolon"], ["--", "a SQL comment"], ["/*", "a block comment"]];
  function whereError(w) {
    const t = (w || "").trim();
    if (!t) return "";
    for (const [tok, described] of WHERE_BANNED) {
      if (t.includes(tok)) return `Remove ${described} — this takes a single condition.`;
    }
    return "";
  }

  useEffect(() => { onEditingChange && onEditingChange(editing !== null); }, [editing, onEditingChange]);

  const valid = editing && editing.data.name.trim() && (editing.data.fields || []).length >= 1
                && !whereError(editing.data.where);
  const saveHint = "Provide a name and select at least one field.";

  function startNew() { setEditing({ index: "new", data: { name: "", fields: [], where: "" } }); }
  function startEdit(idx) { setEditing({ index: idx, data: { ...value[idx], fields: [...(value[idx].fields || [])] } }); }
  function cancel() { setEditing(null); }
  function remove(idx) { onChange(value.filter((_, i) => i !== idx)); }
  function patch(p) { setEditing(e => ({ ...e, data: { ...e.data, ...p } })); }
  function toggle(col) {
    const cur = editing.data.fields || [];
    patch({ fields: cur.includes(col) ? cur.filter(f => f !== col) : [...cur, col] });
  }
  function save() {
    if (!valid) return;
    const row = { name: editing.data.name.trim(), fields: editing.data.fields };
    // Only send `where` when it is actually set, so an ordinary constraint keeps the
    // exact payload shape it has always had.
    const w = (editing.data.where || "").trim();
    if (w) row.where = w;
    if (editing.index === "new") onChange([...value, row]);
    else onChange(value.map((r, i) => i === editing.index ? row : r));
    setEditing(null);
  }

  const editorBody = editing && (
    <>
      <div className="mb-2">
        <label style={{ fontSize: 12, color: "#555" }}>Constraint name</label>
        <input className="form-control" placeholder="e.g. uq_email_org" value={editing.data.name}
          onChange={e => patch({ name: e.target.value })} style={{ fontSize: 13 }} />
      </div>
      <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>
        Fields (tick all that must be unique together)
      </label>
      <FieldPillPicker selected={editing.data.fields} onToggle={toggle} availableColumns={availableColumns} />
      {(editing.data.fields || []).length > 0 && (
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Selected: {editing.data.fields.join(", ")}</div>
      )}

      <div className="mt-3">
        <label style={{ fontSize: 12, color: "#555" }}>Only apply to rows where <span style={{ color: "#888" }}>(optional)</span></label>
        <input className="form-control" style={{ fontSize: 13, fontFamily: "monospace" }}
          placeholder="e.g. status IN ('CONFIRMED','PENDING') AND deleted_at IS NULL"
          value={editing.data.where || ""}
          onChange={e => patch({ where: e.target.value })} />
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
          <i className="bi bi-info-circle" />{" "}
          Leave blank to make the combination unique across every row, forever. Fill it in to make
          the rule apply only to the rows that match — so, for example, a cancelled record no longer
          blocks the same person from signing up again.
        </div>
        {whereError(editing.data.where) && (
          <div style={{ fontSize: 12, color: "#b42318", marginTop: 4 }}>
            <i className="bi bi-exclamation-triangle" /> {whereError(editing.data.where)}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div>
      {value.map((row, idx) => (
        editing && editing.index === idx ? (
          <ConstraintCard key={idx} mode="edit" title="Edit Unique Constraint"
            onCancel={cancel} onSave={save} saveDisabled={!valid} saveHint={saveHint}>
            {editorBody}
          </ConstraintCard>
        ) : (
          <ConstraintCard key={idx} mode="summary" title={row.name || "(unnamed)"}
            canEdit={!hasRows && !editing}
            onEdit={() => startEdit(idx)} onRemove={() => remove(idx)}
            summary={<code style={{ fontSize: 12, color: "#555" }}>
              UNIQUE ({(row.fields || []).join(", ")}){row.where ? <> WHERE {row.where}</> : null}
            </code>} />
        )
      ))}

      {editing && editing.index === "new" && (
        <ConstraintCard mode="edit" title="New Unique Constraint"
          onCancel={cancel} onSave={save} saveDisabled={!valid} saveHint={saveHint}>
          {editorBody}
        </ConstraintCard>
      )}

      {hasRows ? (
        <div style={{ fontSize: 12, color: "#9a6700", background: "#fff8e1", border: "1px solid #ffe39e", borderRadius: 6, padding: "6px 10px", marginTop: 4 }}>
          <i className="bi bi-info-circle" />  Cannot edit or add after creation, you can only remove the constraint.
        </div>
      ) : (
        !editing && (
          <button type="button" onClick={startNew}
            style={{ fontSize: 13, color: "#0d6efd", background: "none", border: "1px solid #0d6efd", borderRadius: 6, padding: "4px 14px", cursor: "pointer", marginTop: 4 }}>
            <i className="bi bi-plus-lg" /> Add Unique Constraint
          </button>
        )
      )}
    </div>
  );
}
